import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useReducer,
    useState,
} from "react";
import { apply, WorkspaceEvent } from "./model/engine";
import { seedWorkspace } from "./model/fixtures";
import { availableYears } from "./model/api";
import { defaultTailTrend } from "./model/forecast";
import { Workspace } from "./model/types";

interface BudgetContextValue {
    workspace: Workspace;
    dispatch: React.Dispatch<WorkspaceEvent>;
    /** Fiscal year shown across all tabs — Track and Budget share it. */
    year: number;
    setYear: (y: number) => void;
    years: number[];
    /** Budget-tab edits that also drive the Track tab, so the budgeted numbers
     * stay identical across the two views. */
    corrections: Record<string, number>;
    corrNotes: Record<string, string>;
    setCorrection: (id: string, amount: number, note: string) => void;
    /** Per-category tail trend (manual override of the default). */
    tailIncrease: Record<string, number>;
    setTailIncrease: (categoryId: string, pct: number) => void;
    tailTrendFor: (categoryId: string) => number;
}

const BudgetContext = createContext<BudgetContextValue | null>(null);

export const BudgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [workspace, dispatch] = useReducer(apply, undefined, seedWorkspace);
    const [year, setYear] = useState<number>(workspace.currentYear);
    const years = useMemo(() => availableYears(workspace), [workspace]);

    const [corrections, setCorrections] = useState<Record<string, number>>({});
    const [corrNotes, setCorrNotes] = useState<Record<string, string>>({});
    const [tailIncrease, setTailIncreaseState] = useState<Record<string, number>>(
        {}
    );
    const setCorrection = useCallback(
        (id: string, amount: number, note: string) => {
            setCorrections((s) => ({ ...s, [id]: amount }));
            setCorrNotes((s) => ({ ...s, [id]: note }));
        },
        []
    );
    const setTailIncrease = useCallback(
        (categoryId: string, pct: number) =>
            setTailIncreaseState((s) => ({ ...s, [categoryId]: pct })),
        []
    );
    const tailTrendFor = useCallback(
        (categoryId: string) =>
            tailIncrease[categoryId] ?? defaultTailTrend(categoryId),
        [tailIncrease]
    );

    const value = useMemo(
        () => ({
            workspace,
            dispatch,
            year,
            setYear,
            years,
            corrections,
            corrNotes,
            setCorrection,
            tailIncrease,
            setTailIncrease,
            tailTrendFor,
        }),
        [
            workspace,
            year,
            years,
            corrections,
            corrNotes,
            setCorrection,
            tailIncrease,
            setTailIncrease,
            tailTrendFor,
        ]
    );
    return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
};

export function useBudget(): BudgetContextValue {
    const ctx = useContext(BudgetContext);
    if (!ctx) throw new Error("useBudget must be used inside <BudgetProvider>");
    return ctx;
}
